<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';
import { StudentFacilitatorInterface } from '@/types/Student';
import { useStudentFacilitatorStore } from '@/store';
import router from '@/router';

interface FacilitatorPropsInterface {
  tableData?: StudentFacilitatorInterface[];
  studentId?: number | undefined;
  loading?: boolean;
}

const props = defineProps<FacilitatorPropsInterface>();

const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();

const facilitatorCount: Ref<number> = computed(() => (facilitators.value ? facilitators.value.length : 0));
const facilitators: Ref<StudentFacilitatorInterface[] | undefined> = toRef(props, 'tableData');
const facilitatorsItems: Ref<{ name: string; value: number }[]> = ref([]);
const isAttach = ref(false);
const isModalOpen = ref(false);
const studentFacilitatorStore = useStudentFacilitatorStore();
const toast = useToast();

const doMessage = (name: string) => {
  toast.add({
    severity: 'success',
    summary: isAttach.value ? 'Added' : 'Removed',
    detail: isAttach.value ? 'Facilitator(s) has been added' : `Facilitator ${name} has been removed from student`,
    life: 3000,
  });
  emit(EMIT.UPDATE);
};

const attachFacilitators: (ids: number[]) => Promise<void> = async (ids) => {
  await studentFacilitatorStore.attachFacilitators({ facilitators: ids, studentId: props.studentId as number });

  isAttach.value = !isAttach.value;

  doMessage('');
};

const onCancel = () => {
  isModalOpen.value = !isModalOpen.value;
};

const openAddFacilitators = async () => {
  if (can([PERMISSIONS.STUDENT_ATTACH_FACILITATORS]) && can([PERMISSIONS.STUDENT_FACILITATORS_AVAILABLE_TO_ADD])) {
    facilitatorsItems.value = await studentFacilitatorStore.getAddFacilitatorsModalData(props.studentId as number);
    isModalOpen.value = !isModalOpen.value;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add facilitators',
      life: 3000,
    });
  }
};

const onSave = (facilitators: { name: string; value: number }[]) => {
  const facilitatorIds = facilitators.map((facilitator) => facilitator.value);
  attachFacilitators(facilitatorIds);
  isModalOpen.value = !isModalOpen.value;
};

const detachFacilitator: (ids: number[], facilitatorName: string) => Promise<void> = async (ids, facilitatorName) => {
  await studentFacilitatorStore.detachFacilitators({ facilitatorsIds: ids, studentId: props.studentId as number });

  isAttach.value = false;

  doMessage(facilitatorName);
};

const createMenuModel = (item: { id: number; display_name: string }) => {
  return [
    {
      label: 'Remove Facilitator',
      command: () => {
        detachFacilitator([item.id], item.display_name);
      },
      visible: can([PERMISSIONS.STUDENT_DETACH_FACILITATORS]),
    },
    {
      label: 'View Profile',
      command: () => {
        router.push(`/user/${item.id}`);
      },
      visible: can([PERMISSIONS.STUDENT_FACILITATORS_SHOW]),
    },
  ].filter((item) => item.visible);
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Facilitators</h2>
      <Chip>{{ facilitatorCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.STUDENT_ATTACH_FACILITATORS])"
          type="text"
          size="small"
          text="Add Facilitator"
          icon-left="add"
          @click="openAddFacilitators"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="facilitators"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-student-facilitator-store-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="facilitatorCount">
          <Column body-class="mw-20rem" header-class="mw-20rem" field="name" header="Facilitator">
            <template #body="{ data }">
              <RouterLink :to="`/user/${data.id}`" class="font-semibold" v-text="data.display_name" />
            </template>
          </Column>
          <Column />
          <Column
            body-style="justify-content: center; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Facilitators not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="isModalOpen && can([PERMISSIONS.STUDENT_FACILITATORS_AVAILABLE_TO_ADD])"
        title="Add Facilitators"
        :items="facilitatorsItems"
        :searchable="true"
        button-text="Add Facilitators"
        placeholder="Search Facilitators"
        person-type="facilitator"
        @add-items="onSave"
        @cancel="onCancel"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped></style>
